'use strict';

import { SDKEvents, IMAEvents } from '../constants/EventList';
import EventBus from '../components/EventBus';
import AdFlow from './AdFlow';
import AdInstance from './AdInstance';
import Macros from '../components/Macros';
import GameRepository from '../repositories/GameRepository';
import { dankLog } from '../utils/dankLog';
import {
  extendDefaults,
  getIframeDepth,
  getParentDomain,
  getParentUrl,
  getScript,
  getTopDomain,
  Ls,
} from '../utils/common';
import cloneDeep from 'lodash.clonedeep';
import { AdType, DefaultInterstitialPlacement, InterstitialPlacements } from '../constants/AdType';
import PluginConsole from '../components/PluginConsole';
import Player from './Player';
import Leaderboards from './Leaderboards';
import Achievements from './Achievements';
import Payments from './Payments';
import Environment from './Environment';
import GameScreen from './GameScreen';

import PackageJSON from '../../package.json';
import Ga8Custom from '../splash/Ga8Custom';
import PlayerSession from './PlayerSession';

const CONSOLE_DOMAINS = ['console.ga8.io', 'console.game8.io'];

// Responses cross the SDK seam via MessagePort.postMessage, which structured-
// clones the value. Domain objects (Player, Payments, ...) carry non-cloneable
// own state — callbacks in `_options`, an IDBDatabase in `_db` — that throws
// DataCloneError, rejecting the SDK call. The SDK rebinds its own methods onto
// the returned data, so it only needs the serializable public fields: drop
// functions and private (`_`-prefixed) state, guarding against cycles.
function toCloneable(value, seen) {
  if (value === null || typeof value !== 'object') {
    return typeof value === 'function' ? undefined : value;
  }
  // An Error keeps `message`/`stack` on non-enumerable own properties, so the
  // Object.keys() walk below would flatten it to `{}` and the game would get a
  // failure with no reason attached.
  if (value instanceof Error) return { name: value.name, message: value.message };
  seen = seen || new WeakSet();
  if (seen.has(value)) return undefined;
  seen.add(value);
  if (Array.isArray(value)) return value.map((v) => toCloneable(v, seen));
  const out = {};
  for (const key of Object.keys(value)) {
    if (key.startsWith('_')) continue;
    if (typeof value[key] === 'function') continue;
    out[key] = toCloneable(value[key], seen);
  }
  return out;
}

// `position` is Google's placement vocabulary verbatim. An unrecognised value falls
// back instead of throwing: this is an API third-party games call, and a typo should
// cost the right placement, not the whole ad break.
function resolveInterstitialPlacement(position) {
  if (!position) return DefaultInterstitialPlacement;
  if (InterstitialPlacements.indexOf(position) !== -1) return position;
  console.warn(
    `g8plugin: unknown ad position "${position}", falling back to "${DefaultInterstitialPlacement}"`,
  );
  return DefaultInterstitialPlacement;
}

class Plugin {
  _childFrameContext = {};
  _gameScreen = null;
  _exitedFullscreen = false;
  // Screen Wake Lock state. `_wakeLock` is the sentinel the browser hands back;
  // `_wakeLockDesired` records that a play session is open, so the lock can be
  // re-acquired after the browser silently drops it (which it does every time
  // the tab is hidden).
  _wakeLock = null;
  _wakeLockDesired = false;
  _wakeLockPending = null;
  _onVisibilityChangeBound = null;
  // The portal overlay can trigger Play before the splash handler is ready.
  // Defer playback until initialization has finished.
  _playHandler = null;
  _resolvePlayReady = null;
  _playReady = new Promise((resolve) => (this._resolvePlayReady = resolve));
  constructor(options) {
    this._defaults = this._getDefaultOptions();
    this._extendDefaultOptions(this._defaults, options);
    this._version = options.version || '0.0.0';
    this._bridge = this._getBridgeContext();
    this._parentURL =
      0 === this._bridge.depth
        ? encodeURIComponent(this._bridge.parentURL)
        : this._bridge.parentURL;
    this._parentDomain = this._bridge.parentDomain;
    this._topDomain = this._bridge.topDomain;
    this._isFullScreen = !1;
    this._giftCount = {
      preroll: 0,
      midroll: 0,
      rewarded: 0,
    };
    // Bind once and keep the reference: removeEventListener matches on identity,
    // so re-binding at teardown time would silently fail to detach.
    if (this.options.keepScreenAwake && typeof document !== 'undefined') {
      this._onVisibilityChangeBound = this._onVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this._onVisibilityChangeBound);
      dankLog('WAKE_LOCK', 'supported: ' + ('wakeLock' in navigator), 'info');
    }
    this._checkConsole();
    this._subscribeToEvents();
    this.sdkReady = new Promise(this._initialize.bind(this));
    this.sdkReady
      .then(async (gameData) => {
        this._sdk_ready = true;
        this._listtenEventFromSDK();
        if (this.options.createGameScreenOnReady !== false)
          this._gameScreen = await this.createGameScreen(this.options, this._gameData);
        this._sendLoaderDataEvent();
        this._checkSplashAndPromoScreens();
      })
      .catch((error) => {
        console.error('SDK failed to initialize', error);
        this._sdk_ready = false;
      })
      .finally(async () => {
        this._resolvePlayReady();
      });
  }

  // Wait for the splash handler so the first Play click is not lost during initialization.
  async play() {
    await this._playReady;
    if (this._playHandler) return this._playHandler();
  }

  _listtenEventFromSDK() {
    window.addEventListener('message', (message) => {
      this._handleEventFromSDK(message);
    });
  }

  async _handleEventFromSDK(message) {
    var data = message.data,
      type = data.type,
      method = data.method,
      // The SDK posts method arguments under `params` (see game8-sdk
      // communication.js); their values are spread as positional args.
      // Keep `options` as a fallback: the deployed `latest` SDK still
      // posts under `options`, so a params-only read would drop every
      // argument until that SDK is promoted to latest too.
      options = data.params || data.options || {};

    if (type === 'g8plugin' && method === 'init') {
      this._childFrameContext = {
        source: message.source,
        origin: message.origin,
      };
      let event = {
        name: 'SDK_READY',
        message: 'Everything is ready.',
        status: 'success',
      };
      this._pushEventToSDK('_onInit', { event: event });
      // `init` is the SDK handshake signal only — don't fall through to the
      // generic dispatch, which would call g8plugin.init() and re-initialize.
      return;
    }

    var obj;
    if (type === 'g8plugin') obj = window.g8plugin;
    else if (type === 'g8-player') obj = await this.getPlayer();
    else if (type === 'g8-leaderboards') obj = await this.getLeaderboards();
    else if (type === 'g8-achievements') obj = await this.getAchievements();

    if (obj) {
      var func = obj[method];
      if (func && typeof func === 'function') {
        try {
          var result = await func.apply(obj, Object.values(options));
          if (message.ports && message.ports[0]) message.ports[0].postMessage(toCloneable(result));
        } catch (error) {
          // Same reason as the success path above: a non-cloneable rejection
          // throws inside this catch, nothing is posted back, and the SDK call
          // hangs until it times out.
          if (message.ports && message.ports[0])
            message.ports[0].postMessage({ error: toCloneable(error) });
        }
      }
    }
  }

  _pushEventToSDK(method, options = {}) {
    try {
      if (this._childFrameContext.source) {
        const msg = {
          type: 'g8sdk',
          method: method,
          options: options,
        };
        this._childFrameContext.source.postMessage(msg, this._childFrameContext.origin);
      }
    } catch (err) {
      console.log('g8plugin forwardEvent err', err);
    }
  }

  _sendLoaderDataEvent() {
    try {
      this.options.onLoaderEvent({
        name: 'LOADER_DATA',
        message: { game: this._gameData, bridge: this._bridge },
        status: this._sdk_ready ? 'success' : 'error',
      });
    } catch (error) {
      console.error('DEVELOPER_ERROR', error.message, 'warning');
    }
  }
  _getDefaultOptions = function () {
    return {
      debug: false,
      testing: false,
      apiDomain: import.meta.env.VITE_API_DOMAIN,
      gameHost: import.meta.env.VITE_GAME_HOST,
      imgHost: import.meta.env.VITE_IMG_HOST,
      maxTimeplay: import.meta.env.VITE_MAX_TIMEPLAY || null,
      // Keep the device screen on while a game is running.
      keepScreenAwake: true,
      intervalPlayerSession: import.meta.env.VITE_INTERVAL_PLAYER_SESSION || 60,
      gameId: '4f3d7d38d24b740c95da2b03dc3a2333',
      prefix: 'g8plugin__',
      onEvent: function (e) {},
      onLoaderEvent: function (e) {},
      flashSettings: {
        adContainerId: '',
        splashContainerId: '',
      },
      advertisementSettings: {},
      resumeGame: function () {},
      pauseGame: function () {},
      onInit: function (e) {},
      onError: function (e) {},
      loader: {},
      container: 'game-screen',
      // New callback functions for game interactions
      getGameReaction: function () {
        console.error('getGameReaction is not implemented');
        return null;
      },
      setGameReaction: function (value) {
        console.error('setGameReaction is not implemented');
      },
      isFavorite: function () {
        console.error('isFavorite is not implemented');
        return null;
      },
      favoriteGame: function () {
        console.error('favoriteGame is not implemented');
      },
      unfavoriteGame: function () {
        console.error('unfavoriteGame is not implemented');
      },
      allowPlayGame: function () {
        console.error('allowPlayGame is not implemented');
        return false;
      },
      toggleFeedback: function () {
        console.error('toggleFeedback is not implemented');
      },
      // Initial state of the expand button, see gameControls.expandButton.
      expanded: false,
      onExpandToggle: function () {
        console.error('onExpandToggle is not implemented');
      },
    };
  };
  _extendDefaultOptions = function (defaults, options) {
    let target = cloneDeep(defaults);
    this.options = options ? extendDefaults(target, options) : target;
    this.options.gameId = this.options.gameId.trim();
  };

  _checkConsole() {
    try {
      if (!Ls.available) return;

      // Enable debugging when the game is embedded in our console.
      if (CONSOLE_DOMAINS.includes(this._parentDomain)) {
        Ls.set('g8_debug', true);
      }

      // Open the debug console when debugging is enabled.
      if (Ls.getBoolean('g8_debug')) {
        this.openConsole();
      }
    } catch (error) {
      console.error(error);
    }
  }

  _getBridgeContext() {
    const parentURL = getParentUrl();
    const parentDomain = getParentDomain();
    const topDomain = getTopDomain();
    return {
      parentURL,
      parentDomain,
      topDomain,
      depth: getIframeDepth(),
      domainMatched: parentDomain === topDomain,
    };
  }

  _getSplashTemplate(gameData) {
    return Ga8Custom;
  }

  _safeInit = function () {
    this._loadGoogleAnalytics();
  };
  async _initialize(resolve, reject) {
    try {
      this._gameData = await this._getGameData();
      // this._loadCMP();

      this._safeInit();
      // this._checkGameId(),
      // this._checkGameDeleted(),
      // this._checkBlocking(),
      // this._changeMidrollInDebugMode(),

      this.videoAdReady = await this._initializeVideoAd();
      const player = await this.getPlayer();
      this._playerSession = new PlayerSession(player, this.options);

      this._sendSDKReady();

      resolve(this._gameData);
    } catch (error) {
      console.error('SDK failed to initialize', error);
      this._sendSDKError(error);

      // Just resume the game.
      this.onResumeGame(error.message, 'warning');

      // Something went wrong.
      reject(error);
    }
  }
  // Ask the OS to keep the screen on for the duration of the play session.
  // Games with long touch-free stretches (splash, ad breaks, turn-based play)
  // otherwise get the display switched off mid-session.
  async _acquireWakeLock() {
    if (!this.options || !this.options.keepScreenAwake) return;

    // The API only exists in a secure context (https or localhost). Over plain
    // http it is simply absent — worth logging, since that is indistinguishable
    // from "the code never ran" when testing by hand on a phone.
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) {
      dankLog('WAKE_LOCK', 'unsupported here (needs a secure context)', 'info');
      return;
    }

    this._wakeLockDesired = true;
    if (this._wakeLock && !this._wakeLock.released) return;
    if (this._wakeLockPending) return this._wakeLockPending;

    this._wakeLockPending = navigator.wakeLock
      .request('screen')
      .then((sentinel) => {
        // The browser releases the lock by itself whenever the page stops being
        // visible. Track that so _onVisibilityChange knows to ask again.
        if (sentinel && typeof sentinel.addEventListener === 'function') {
          sentinel.addEventListener('release', () => {
            dankLog('WAKE_LOCK', 'dropped by the browser', 'info');
            this._wakeLock = null;
          });
        }
        if (!this._wakeLockDesired) {
          if (sentinel && typeof sentinel.release === 'function') sentinel.release();
          return;
        }
        this._wakeLock = sentinel;
        dankLog('WAKE_LOCK', 'acquired', 'success');
      })
      .catch((e) => {
        // Low battery, power-saving mode or an unsupported browser: a refusal is
        // a valid answer from the OS, not a failure. Same posture as the screen
        // orientation lock in GameScreen._handleOrientationChange.
        dankLog('WAKE_LOCK', 'request refused: ' + e.message, 'warning');
      })
      .finally(() => {
        this._wakeLockPending = null;
      });

    return this._wakeLockPending;
  }

  async _releaseWakeLock() {
    this._wakeLockDesired = false;
    const sentinel = this._wakeLock;
    this._wakeLock = null;
    if (!sentinel || sentinel.released) return;
    try {
      await sentinel.release();
      dankLog('WAKE_LOCK', 'released', 'info');
    } catch (e) {
      dankLog('WAKE_LOCK', 'release failed: ' + e.message, 'warning');
    }
  }

  // Hidden tabs lose the lock unconditionally, so without this the feature dies
  // the first time the player answers a message and comes back.
  async _onVisibilityChange() {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
    if (!this._wakeLockDesired) return;
    await this._acquireWakeLock();
  }

  async _teardownWakeLock() {
    await this._releaseWakeLock();
    if (this._onVisibilityChangeBound) {
      document.removeEventListener('visibilitychange', this._onVisibilityChangeBound);
      this._onVisibilityChangeBound = null;
    }
  }

  destroy = async function () {
    // First: the teardown below still reads `this._gamescreen`, which never
    // matches the `_gameScreen` field, so everything after it is a no-op today.
    await this._teardownWakeLock();

    if (this._player) {
      await this._player.destroy();
      this._player = null;
    }
    if (this._gameScreen) {
      await this._gameScreen.destroy();
      this._gameScreen = null;
    }
    if (this._playerSession) {
      await this._playerSession.destroy();
      this._playerSession = null;
    }
    this._gameData = null;
    this._gameRepository = null;
    this._achievements = null;
    this._leaderboards = null;
    this._payments = null;
  };
  getGameRepository = function () {
    if (!this._gameRepository) this._gameRepository = new GameRepository(this.options);
    return this._gameRepository;
  };
  _setNestedProperty = (obj, path, value) => {
    const keys = path.split('.');
    keys.reduce((acc, key, index) => {
      if (index === keys.length - 1) {
        acc[key] = value;
      } else {
        acc[key] = acc[key] || {};
      }
      return acc[key];
    }, obj);
  };
  _wildcardMatch = (pattern, string) => {
    // Escape special regex characters in the pattern, then replace wildcards
    var regexPattern = pattern
      .replace(/[.+^${}()|[\]\\]/g, '\\$&') // Escape regex special chars
      .replace(/\*/g, '.*') // * -> .*
      .replace(/\?/g, '.') // ? -> .
      .replace(/\+/g, '.+'); // + -> .+
    if (pattern.startsWith('!')) {
      regexPattern = regexPattern
        .replace(/^!/, '^((?!') // ! at the start negates the condition
        .replace(/$/, ').*)$'); // Closing the negative group
    }

    const regex = new RegExp(regexPattern);
    return regex.test(string);
  };
  _getGameData = async function () {
    var gameData = await this.getGameRepository().getGameData();

    const attibutes = gameData.attributes || [];
    const host = this._parentDomain;
    const platform = this._environment.platform;
    const language = this._environment.language;

    // compute the value of each attribute base on the conditions
    attibutes.forEach((attribute) => {
      let attributeValue = attribute.value;
      let defaultValue = attributeValue.default.value;
      this._setNestedProperty(gameData, attribute.name, defaultValue);

      for (let i = 0; i < attributeValue.conditionalValues?.length; i++) {
        let conditions = attributeValue.conditionalValues[i].conditions;
        let match = true;
        if (match && conditions.host) {
          match = this._wildcardMatch(conditions.host, host);
        }
        if (match && conditions.platform) {
          match = this._wildcardMatch(conditions.platform, platform);
        }
        if (match && conditions.language) {
          match = this._wildcardMatch(conditions.language, language);
        }
        if (match) {
          let value = attributeValue.conditionalValues[i].value;
          this._setNestedProperty(gameData, attribute.name, value);
          break;
        }
      }
    });

    console.log('gameData', gameData);

    return gameData;
  };
  _createSplash(gameData, isConsentDomain) {
    const ActiveSplash = this._getSplashTemplate(gameData);
    let splash = new ActiveSplash(
      {
        ...this.options,
        isConsentDomain,
        version: PackageJSON.version,
        flashSettings: {
          splashContainerId: this._gameScreen._splashContainer.id,
        },
      },
      gameData,
    );
    const onPlay = async () => {
      if (!this.options.allowPlayGame()) {
        return;
      }

      var _start = function () {
        if (this._playerSession && !this._playerSession.timer) {
          this._playerSession.start();
        }

        if (
          this.options.maxTimeplay === null ||
          this._playerSession.totalToday < this.options.maxTimeplay
        ) {
          splash.hide();
          this._gameScreen.showGameFrame();
          this.requestFullScreenIfMobile();
          this.onResumeGame('Advertisement(s) are done. Start / resume the game.', 'success');
          this.logPlayHistory();
        } else {
          console.log('Player has exceeded daily time limit');
        }
      };

      if (this._gameData.enableAds[AdType.Interstitial]) {
        this.showAd(AdType.Interstitial, { position: 'preroll' })
          .catch((reason) => {
            console.error(reason);
          })
          .finally(() => {
            _start.bind(this)();
          });
      } else {
        _start.bind(this)();
      }
    };

    // The portal overlay covers the splash button, so expose playback without a DOM click.
    // Emit playClick so the splash also hides its button and shows the loader during preroll.
    this._playHandler = () => splash.emit('playClick');
    splash.on('playClick', onPlay);

    // Now pause the game.
    this.onPauseGame('Pause the game and wait for a user gesture', 'success');

    // Make sure the container is removed when an ad starts.
    this.eventBus.subscribe('SDK_GAME_PAUSE', () => {
      splash.hide();
    });

    // Make sure the container is removed when the game is resumed.
    this.eventBus.subscribe('SDK_GAME_START', () => {
      splash.hide();
    });
  }

  requestFullScreenIfMobile() {
    if (this.getEnvironment().platform === 'MOBILE_WEB' && this._gameData.playFullScreenOnMobile) {
      this._gameScreen._requestFullScreen();
    }
  }

  async _initializeVideoAd() {
    var t = this._gameData;
    extendDefaults(this.options.advertisementSettings, t.sdk);
    ((this.macros = new Macros({
      game: t,
      bridge: this._bridge,
    })),
      (this.adInstance = new AdInstance(
        this.options.flashSettings.adContainerId,
        this.options.advertisementSettings,
        {
          parentURL: this._parentURL,
          parentDomain: this._parentDomain,
          topDomain: this._topDomain,
        },
      )),
      (this.adInstance.gameId = t.gameId),
      (this.adInstance.category = t.category),
      (this.adInstance.tags = t.tags),
      (this.adInstance.macros = this.macros),
      (this.adInstance.gameData = t),
      // this.adInstance.PackageJSON = mc,
      await this.adInstance.start());
  }
  _onEvent = function (e) {
    // console.log("_onEvent:", e.name, e.message, e.status);
    try {
      let data = {
        name: e.name,
        message: e.message,
        status: e.status,
        skipForward: e.skipForward,
        adPosition: e.adPosition,
      };
      this.options.onEvent(data);
      if (!data.skipForward) this._pushEventToSDK('_onEvent', { event: data });
    } catch (e) {
      console.error('DEVELOPER_ERROR', e.message, 'warning');
    }
  };
  _loadGoogleAnalytics = function () {
    var scripts = ['https://www.googletagmanager.com/gtag/js?id=G-5MW4F8LNVC'];
    getScript(scripts[0], 'g8plugin_google_analytics', {
      alternates: scripts,
      error_prefix: 'Blocked:',
      exists: function () {
        return window.dataLayer;
      },
    })
      .then(function () {
        window.dataLayer = window.dataLayer || [];
        // gtag has to push the arguments object itself — gtag.js ignores a plain
        // array. Exposing it globally is what lets the ad code report break results.
        if (typeof window.gtag !== 'function') {
          window.gtag = function () {
            window.dataLayer.push(arguments);
          };
        }
        window.gtag('js', new Date());
        window.gtag('config', 'G-5MW4F8LNVC');
      })
      .catch(
        function (e) {
          console.log(e);
          this._sendSDKError(e);
        }.bind(this),
      );
  };
  _subscribeToEvents = function () {
    this.eventBus = new EventBus();

    SDKEvents.forEach((eventName) => {
      this.eventBus.subscribe(eventName, (event) => this._onEvent(event), 'sdk');
    });

    IMAEvents.forEach((eventName) => {
      this.eventBus.subscribe(eventName, (event) => this._onEvent(event), 'ima');
    });

    this.eventBus.subscribe(
      'COMPLETE',
      function () {
        try {
          let message = {
            type: 'SDK_IMPLEMENTED',
            gameID: this.options.gameId,
          };
          if (window.location !== window.top.location) window.top.postMessage(message, '*');
          else {
            if (window.opener && window.opener.location !== window.location)
              window.opener.postMessage(message, '*');
          }
        } catch (e) {
          console.error(e);
        }
      }.bind(this),
    );

    this.eventBus.subscribe(
      'SDK_REWARDED_WATCH_COMPLETE',
      function () {
        try {
          let message = {
            type: 'SDK_REWARDED_WATCH_COMPLETE',
            gameID: this.options.gameId,
          };
          if (window.location !== window.top.location) window.top.postMessage(message, '*');
          else {
            if (window.opener && window.opener.location !== window.location)
              window.opener.postMessage(message, '*');
          }
        } catch (e) {
          console.error(e);
        }
      }.bind(this),
    );

    this.eventBus.subscribe(
      'CONTENT_PAUSE_REQUESTED',
      function () {
        return this.onPauseGame('New advertisements requested and loaded', 'success');
      }.bind(this),
      'ima',
    );
  };
  _checkSplashAndPromoScreens = function () {
    if (this._gameData.loader && this._gameData.loader.mobile === false && this._getisMobile())
      this._gameData.loader.enabled = false;
    const gameData = this._gameData;
    var isConsentDomain = gameData.gdpr;
    var loader = gameData.loader;

    if (this.options.preload && loader.enabled) this._createSplash(gameData, isConsentDomain);
    else {
      this._gameScreen.showGameFrame();
      this.onResumeGame('Advertisement(s) are done. Start / resume the game.', 'success');
      this.logPlayHistory();
    }
  };
  _sendSDKReady = function () {
    var message = 'Everything is ready.';
    this.eventBus.broadcast('SDK_READY', {
      message: message,
      status: 'success',
    });
    try {
      this.options.onInit(message);
    } catch (e) {
      dankLog('DEVELOPER_ERROR', e.message, 'warning');
    }
  };
  _sendSDKError = function (e) {
    var ex = e.message ? e : { message: e };
    this.eventBus.broadcast('SDK_ERROR', {
      message: ex.message,
      status: 'error',
    });
    try {
      this.options.onError(ex);
    } catch (e) {
      dankLog('DEVELOPER_ERROR', e.message, 'warning');
    }
    this._pushEventToSDK('_onError', { ex: ex });
  };
  onResumeGame = function (message, status) {
    if (this._exitedFullscreen) {
      this.onPauseGame('Wait for the player to continue after exiting fullscreen.', 'success');
      return;
    }
    try {
      this.options.resumeGame();
    } catch (e) {
      console.log('g8-adflow error onResumeGame', e);
      dankLog('DEVELOPER_ERROR', e.message, 'warning');
    }
    // The game is actually running now (pre-roll, if any, is done). Deliberately
    // not paired with onPauseGame: pauses are mostly ad breaks, which is exactly
    // when the player is watching without touching anything.
    this._acquireWakeLock();
    var eventName = 'SDK_GAME_START';
    this.eventBus.broadcast(eventName, {
      name: eventName,
      message: message,
      status: status,
    });
    this._pushEventToSDK('_onResumeGame');
  };
  onPauseGame = function (message, status) {
    try {
      this.options.pauseGame();
    } catch (e) {
      dankLog('DEVELOPER_ERROR', e.message, 'warning');
    }
    // Stop the player session timer
    if (this._playerSession && this._playerSession.timer) {
      this._playerSession.stop();
    }
    var name = 'SDK_GAME_PAUSE';
    this.eventBus.broadcast(name, {
      name: name,
      message: message,
      status: status,
    });
    this._pushEventToSDK('_onPauseGame');
  };

  /**
   * openConsole
   * Enable debugging, we also set a value in localStorage,
   * so we can also enable debugging without setting the property.
   * This is nice for when we're trying to debug a game that is not ours.
   * @public
   */
  openConsole() {
    try {
      const sdkConsole = new PluginConsole(this);
      sdkConsole.start();
      Ls.set('g8_debug', true);
    } catch (error) {
      console.log(error);
    }
  }

  async showAd(adType, params = {}) {
    return new Promise((resolve, reject) => {
      var options = {
        adType: adType || AdType.Interstitial,
        params: Object.assign({}, params),
        // adTimestamp: this.adRequestTimer
      };

      if (options.adType === AdType.Interstitial) {
        options.params.position = resolveInterstitialPlacement(options.params.position);
      }

      var enableAds = this._gameData.enableAds[options.adType];
      if (enableAds == null) enableAds = this._gameData.enableAds['default'];
      if (enableAds == null) enableAds = true;
      if (!enableAds) {
        return reject('Advertisement is disabled for this game.');
      }

      // Preroll and in-game breaks share adType 'interstitial', so `enableAds.interstitial`
      // cannot stop one without the other. `next` is the switch for in-game breaks;
      // absent means on.
      if (
        options.adType === AdType.Interstitial &&
        options.params.position !== 'preroll' &&
        this._gameData.enableAds['next'] === false
      ) {
        return reject('In-game advertisements are disabled for this game.');
      }

      if (this.adFlowRunning) {
        return reject("An advertisement is already running. Can't show another one.");
      }

      this.adFlowRunning = true;
      var adFlow = new AdFlow(this);
      adFlow
        .runAdFlow(options)
        .then((result) => {
          this.adFlowRunning = false;
          if (result.success) {
            return resolve(result);
          } else {
            return reject(result);
          }
        })
        // Without this the flag stays set for the rest of the session and every
        // later showAd() is refused as "already running".
        .catch((error) => {
          this.adFlowRunning = false;
          return reject({
            success: false,
            args: { message: (error && error.message) || String(error), status: 'error' },
          });
        });
    });
  }

  /**
   * preloadRewarded
   * Preload a rewarded ad. By default we preload interstitials.
   * The developer can use this method to check for rewarded ads availability.
   * We have to do this due to low fill rate of rewarded ads.
   * This way the developer can decide whether to show a rewarded ads button within their game.
   * @param {String} adType
   * @return {Promise<any>}
   * @public
   */
  async preloadAd(adType) {
    return async (resolve, reject) => {
      try {
        const gameData = await this.sdkReady;

        await self.videoAdReady;

        adType = adType || AdType.Rewarded;
        // Check ad type
        if (adType !== AdType.Interstitial && adType !== AdType.Rewarded) {
          throw new Error('Unsupported an advertisement type:' + adType);
        }

        const result = await this.adInstance.preloadAd(adType);
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };
  }

  /**
   * cancelAd
   * Cancels the current loaded/ running advertisement.
   * @return {Promise<void>}
   */
  async cancelAd() {
    return async (reject, resolve) => {
      try {
        this.adInstance.cancel();
        resolve();
      } catch (error) {
        reject(error.message);
      }
    };
  }

  _getisMobile() {
    let check = false;
    (function (a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
          a,
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4),
        )
      )
        check = true;
    })(navigator.userAgent || navigator.vendor || window.opera);

    if (check === false && window.orientation > -1) check = true;
    return check;
  }

  async getPlayer(options = { signed: false }) {
    // If we have a cached player and don't need a signed version, return it
    if (this._player && !options.signed) return this._player;

    // If we need a signed version and already have a player with signature, return it
    if (this._player && options.signed && this._player.signature) return this._player;

    if (!this._playerPromise) {
      this._playerPromise = this._createPlayer();
    }

    const player = await this._playerPromise;
    if (options.signed) {
      const signature = await player.sign();
      player.signature = signature;
    }

    this._player = player;

    return this._player;
  }

  async _createPlayer() {
    const player = new Player(this.options);
    await player.databaseReady();
    await player.initPlayer();
    return player;
  }

  async getLeaderboards() {
    // make sure we have a player
    const player = await this.getPlayer();

    // if we already have a leaderboards instance, return it
    if (this._leaderboards) return this._leaderboards;

    // create a new leaderboards instance
    this._leaderboards = new Leaderboards(this.options, player);
    return this._leaderboards;
  }

  async getAchievements() {
    // make sure we have a player
    const player = await this.getPlayer();

    // if we already have an achievements instance, return it
    if (this._achievements) return this._achievements;

    // create a new achievements instance
    this._achievements = new Achievements(this.options, player);
    return this._achievements;
  }

  async getPayments() {
    // make sure we have a player
    const player = await this.getPlayer();

    // if we already have a payments instance, return it
    if (this._payments) return this._payments;

    // create a new payments instance
    this._payments = new Payments(this.options, player);
    return this._payments;
  }

  getEnvironment() {
    if (this._environment) return this._environment;

    this._environment = new Environment();
    return this._environment;
  }

  async logPlayHistory() {
    if (!this._player)
      throw new Error(
        'Player not initialized. Please call getPlayer() before calling logPlayHistory().',
      );

    await this._player.logPlayHistory();
  }

  setExpanded(expanded) {
    if (this._gameScreen) this._gameScreen.setExpanded(expanded);
  }

  async createGameScreen(options, gameData) {
    const gameScreen = new GameScreen(
      {
        ...options,
        onFullscreenExit: () => this.exitGame(),
      },
      gameData,
    );
    await gameScreen.init();
    return gameScreen;
  }

  // Leaving fullscreen always hands the page back its overlay — no state is consulted, so
  // the player is never left staring at a splash with no way back.
  exitGame() {
    this._exitedFullscreen = true;
    this.onPauseGame('The player exited the fullscreen game view.', 'success');
    // Ad-break pauses last seconds; this one lasts until the player comes back, so the
    // session's focus/visibility listeners must not restart the timer behind the overlay.
    // _playerSession.start() in resumeGameAfterExit() registers them again.
    this._playerSession?.unregisterVisibilityChange();
    if (typeof this.options.onExitGame === 'function') this.options.onExitGame();
  }

  async resumeGameAfterExit() {
    this._gameScreen?._requestFullScreen();
    this._exitedFullscreen = false;
    if (this._playerSession && !this._playerSession.timer) this._playerSession.start();
    this.onResumeGame('The player resumed the current game session.', 'success');
  }
}

export default Plugin;
